import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { type FC, useMemo, useState } from "react";
import { useSupabaseLoader } from "../../../../../../../server/supabase/hooks";
import type { UUID } from "../../../../../../../utils/uuid";
import { WhatsAppTemplatePreview } from "../../../../../../whatsapp-moment/components/whatsapp-template-preview";
import {
  SupabaseWhatsAppMomentService,
  SupabaseWhatsAppTemplateService,
} from "../../../../../../whatsapp-moment/server/whatsapp-moment-service";
import type {
  WhatsAppDestinationType,
  WhatsAppTemplate,
} from "../../../../../../whatsapp-moment/types";
import { SupabaseMomentService } from "../../../../../server/supabase-moment-service";
import { NotSet } from "../components/not-set";
import { useBuiltMjmlMessage } from "../hooks/use-built-mjml-message";
import { useEmailPreviewSource } from "../hooks/use-email-preview-source";
import { usePreviewTags } from "../hooks/use-preview-tags";
import { usePreviewContext } from "../preview-context";
import { EmailDesktopPreivew } from "./email-desktop-preview";
import { EmailMobilePreview } from "./email-mobile-preview";

const MessagePreview: FC<{ template: WhatsAppTemplate }> = ({ template }) => {
  const {
    moment_preview_data: { dynamic_segment_id_map },
    preview_info,
    preview_test_data,
  } = usePreviewContext();
  const {
    design,
    subject,
    additional_merge_fields: email_additional_merge_fields,
    use_unlayer_email_preview,
  } = useEmailPreviewSource();
  const previewConfig = useMemo(
    () => ({
      loading: false as const,
      additional_merge_fields:
        typeof template.variable_mappings === "object" &&
        template.variable_mappings !== null &&
        !Array.isArray(template.variable_mappings)
          ? Object.values(template.variable_mappings)
              .filter(
                (mergeTag): mergeTag is string =>
                  typeof mergeTag === "string" && mergeTag !== "cta_route",
              )
              .map((mergeTag) => (mergeTag.includes(".") ? mergeTag : `recipient.${mergeTag}`))
          : [],
    }),
    [template.variable_mappings],
  );
  const { data: previewTags } = usePreviewTags({
    dynamic_segment_id_map,
    preview_info,
    preview_test_data,
    design,
    subject,
    email_additional_merge_fields,
    use_unlayer_email_preview,
    config: previewConfig,
  });
  const previewValues = useMemo(
    () => ({
      ...template.content.variables,
      ...Object.fromEntries((previewTags?.tags ?? []).map((item) => [item.key, item.value ?? ""])),
    }),
    [previewTags?.tags, template.content.variables],
  );
  return (
    <WhatsAppTemplatePreview
      template={template}
      heading="Message preview"
      previewValues={previewValues}
    />
  );
};

const PagePreviewBanner: FC = () => {
  const [open, setOpen] = useState(true);

  if (!open) return null;

  return (
    <Alert
      severity="warning"
      icon={<WarningAmberOutlined fontSize="inherit" />}
      action={
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Button
            variant="text"
            color="inherit"
            data-analytics-id="whatsapp-preview-banner-more"
            sx={{ minWidth: 0, p: 0, fontWeight: 700, textTransform: "none" }}
          >
            Find out more
          </Button>
          <IconButton
            aria-label="close"
            color="inherit"
            size="small"
            data-analytics-id="whatsapp-preview-banner-close"
            onClick={() => setOpen(false)}
          >
            <Box component="span" sx={{ fontSize: 16, lineHeight: 1 }}>
              ×
            </Box>
          </IconButton>
        </Stack>
      }
      sx={{
        alignItems: "center",
        borderRadius: 0,
        borderTop: 1,
        borderColor: "warning.light",
        px: 2,
        py: 0.75,
      }}
    >
      <AlertTitle sx={{ mb: 0, fontSize: "0.875rem" }}>
        This view confirms content, not final layout
      </AlertTitle>
    </Alert>
  );
};

const PagePreview: FC = () => {
  const {
    moment_preview_data: {
      email_design_id,
      account_id,
      poll_ids,
      design: form_design,
      dynamic_segment_id_map,
    },
    preview_info,
    preview_test_data,
    previewMode,
  } = usePreviewContext();
  const {
    design: preview_design,
    subject,
    additional_merge_fields: email_additional_merge_fields,
    use_unlayer_email_preview,
  } = useEmailPreviewSource();

  const { loading, data } = useBuiltMjmlMessage({
    email_design_id,
    account_id,
    poll_ids,
    form_design,
    dynamic_segment_id_map,
    preview_info,
    preview_test_data,
    preview_design,
    subject,
    email_additional_merge_fields,
    use_unlayer_email_preview,
    channel: "whatsapp",
  });

  if (loading) return <LinearProgress />;
  if (!data) return <NotSet />;

  return (
    <Stack spacing={1} sx={{ flexGrow: 1, minHeight: 0 }}>
      <Typography variant="overline" sx={{ px: 4 }} color="textSecondary">
        Page preview
      </Typography>
      <PagePreviewBanner />
      <Box
        sx={{
          border: 1,
          borderColor: "divider",
          overflow: "hidden",
          bgcolor: "background.paper",
          flexGrow: 1,
          minHeight: 0,
        }}
      >
        {previewMode === "desktop" ? (
          <EmailDesktopPreivew data={data} />
        ) : (
          <EmailMobilePreview data={data} />
        )}
      </Box>
    </Stack>
  );
};

const DestinationContent: FC<{
  destinationType: Exclude<WhatsAppDestinationType, "page">;
}> = ({ destinationType }) => (
  <Stack spacing={1} sx={{ px: 4, pb: 3 }}>
    <Typography variant="overline" color="textSecondary">
      Destination content
    </Typography>
    <Alert severity="info">
      {destinationType === "content_library"
        ? "This message links to a file in the Content Library. The file is not shown in this preview."
        : "This message links to an external URL. The external page is not shown in this preview."}
    </Alert>
  </Stack>
);

export const PreviewBodyWhatsAppContent: FC<{ momentId: UUID }> = ({ momentId }) => {
  const { data: moment, loading: momentLoading } = useSupabaseLoader(
    async ({ supabase }) => {
      const { data, error } = await new SupabaseMomentService(supabase).maybeGet(momentId);
      if (error) throw error;
      return data;
    },
    [momentId],
  );

  const { data: template, loading: templateLoading } = useSupabaseLoader(
    async ({ supabase }) => {
      if (!moment?.whatsapp_template_id) return null;

      const { data, error } = await new SupabaseWhatsAppTemplateService(supabase).maybeGet(
        moment.whatsapp_template_id,
      );
      if (error) throw error;

      return data;
    },
    [moment?.whatsapp_template_id],
  );

  const { data: destination, loading: destinationLoading } = useSupabaseLoader(
    async ({ supabase }) => {
      if (!moment?.whatsapp_moment_id) return null;

      const { data, error } = await new SupabaseWhatsAppMomentService(supabase).maybeGet(
        moment.whatsapp_moment_id,
      );
      if (error) throw error;

      return data;
    },
    [moment?.whatsapp_moment_id],
  );

  if (momentLoading || templateLoading || destinationLoading) return <LinearProgress />;
  if (!moment || !template) return <NotSet />;

  const savedDestinationType = destination?.destination_type;
  const destinationType: WhatsAppDestinationType =
    savedDestinationType === "content_library" || savedDestinationType === "url"
      ? savedDestinationType
      : "page";
  const hasPageDestination = destinationType === "page";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <Box sx={{ px: 4, py: 3 }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {hasPageDestination
            ? "Review your WhatsApp message and Page before sending"
            : "Review your WhatsApp message and destination before sending"}
        </Typography>
        <MessagePreview template={template} />
      </Box>

      {hasPageDestination ? (
        <PagePreview />
      ) : (
        <DestinationContent destinationType={destinationType} />
      )}
    </Box>
  );
};
